<template>
  <div class="test-login-page">
    <div class="test-login-card">
      <h2 class="title">{{ t('testLogin.title') }}</h2>
      <p class="subtitle">{{ t('testLogin.wechatLoginHint') }}</p>

      <div class="login-form">
        <div class="form-item">
          <label class="label">{{ t('testLogin.wechatLoginCodeLabel') }}</label>
          <input
            v-model.trim="loginCode"
            class="input"
            type="text"
            :placeholder="t('testLogin.wechatLoginCodePlaceholder')"
          />
        </div>
      </div>

      <div class="actions">
        <button class="btn" :disabled="loading" @click="handleLogin">
          {{ loading ? t('testLogin.loggingIn') : t('testLogin.loginButton') }}
        </button>
      </div>

      <div v-if="result" class="result">
        <div class="result-title">{{ t('testLogin.loginResultTitle') }}</div>
        <pre class="result-content">{{ result }}</pre>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { getText } from '@/utils/i18nText'
import { session } from '@/utils/zfgl-lite'
import baseUrl from '@/utils/baseUrl'

export default {
  name: 'TestLogin',
  data() {
    return {
      loginCode: '',
      loading: false,
      result: '',
      baseUrl: (baseUrl.apiBase || 'https://www.ylnkzh.cn').replace(/\/+$/, ''),
      jsessionId: ''
    }
  },
  methods: {
    t(path) {
      return getText(path)
    },
    buildAuthHeaders(extra = {}) {
      const token = sessionStorage.getItem('token') || ''
      return {
        Accept: 'application/json; charset=utf-8',
        Referer: `${this.baseUrl}/ylnch/`,
        sessionType: 'header',
        ...(token ? { 'X-CSRF-TOKEN': token, Authorization: token } : {}),
        ...extra
      }
    },
    persistAuthFromResponse(result = {}) {
      const auth = (result.data && result.data.data) || result.data || {}
      const token = auth.token || result.token || ''
      const headerName = auth.headerName || 'X-CSRF-TOKEN'
      const parameterName = auth.parameterName || '_csrf'

      if (token) {
        sessionStorage.setItem('token', token)
        sessionStorage.setItem('X-CSRF-TOKEN', token)
        sessionStorage.setItem('WECHAT_AUTH_TOKEN', token)
        sessionStorage.setItem('WECHAT_AUTH_HEADER_NAME', headerName)
        sessionStorage.setItem('WECHAT_AUTH_PARAMETER_NAME', parameterName)
        try {
          if (session && typeof session.setToken === 'function') {
            session.setToken(token)
          }
        } catch (e) {
          // ignore
        }
      }

      return auth
    },
    captureJsessionFromResponse(resp) {
      const setCookie = resp?.headers?.['set-cookie'] || resp?.headers?.['Set-Cookie'] || ''
      const cookieText = Array.isArray(setCookie) ? setCookie.join('; ') : String(setCookie || '')
      const matched = cookieText.match(/JSESSION=([^;]+)/i)
      if (matched && matched[1]) {
        this.jsessionId = matched[1]
        sessionStorage.setItem('JSESSION', matched[1])
      }
    },
    async verifyLoginStatus() {
      const resp = await axios.get(`${this.baseUrl}/uoc/umc/user/my/q/simpleinfo`, {
        headers: this.buildAuthHeaders(),
        withCredentials: true
      })
      return resp.data || {}
    },
    async finalizeLogin(loginResult) {
      const auth = this.persistAuthFromResponse(loginResult || {})
      let verifyResult = null
      try {
        verifyResult = await this.verifyLoginStatus()
      } catch (error) {
        verifyResult = {
          status: -1,
          msg: this.t('testLogin.verifyLoginFailed'),
          error: error?.response?.data || String(error)
        }
      }

      this.result = JSON.stringify(
        {
          auth: {
            token: auth.token || sessionStorage.getItem('token') || '',
            headerName: auth.headerName || sessionStorage.getItem('WECHAT_AUTH_HEADER_NAME') || 'X-CSRF-TOKEN',
            phone: auth.phone || '',
            userId: auth.userId || auth.id || ''
          },
          verify: verifyResult
        },
        null,
        2
      )

      if (Number(verifyResult?.status) === 0) {
        this.$message.success(this.t('testLogin.loginSuccess'))
        const redirect = this.$route.query.redirect
        if (redirect) {
          this.$router.push(String(redirect))
        } else {
          this.$router.push('/index')
        }
      } else if (Number(loginResult?.status) === 0) {
        this.$message.warning(this.t('testLogin.verifyLoginFailed'))
      }
    },
    async handleLogin() {
      const code = (this.loginCode || '').trim()
      if (!code) {
        this.$message.warning(this.t('testLogin.errWechatLoginCodeRequired'))
        return
      }

      this.loading = true
      this.result = ''
      try {
        const resp = await axios.get(`${this.baseUrl}/umc/j_wechat_check`, {
          params: { code },
          headers: {
            Accept: 'application/json; charset=utf-8',
            Referer: `${this.baseUrl}/ylnch/`
          },
          withCredentials: true
        })

        this.captureJsessionFromResponse(resp)
        const data = resp.data || {}

        if (data.status === 0) {
          await this.finalizeLogin(data)
        } else {
          this.result = JSON.stringify(data, null, 2)
          this.$message.error(data.msg || this.t('testLogin.loginFailed'))
        }
      } catch (e) {
        this.result = e && e.response
          ? JSON.stringify(e.response.data || {}, null, 2)
          : String(e)
        this.$message.error(this.t('testLogin.loginFailed'))
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.test-login-page {
  min-height: 100vh;
  background: #f5f7fa;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
  box-sizing: border-box;
}

.test-login-card {
  width: 420px;
  max-width: 100%;
  background: #ffffff;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  padding: 24px 24px 20px;
  box-sizing: border-box;
}

.title {
  margin: 0 0 8px;
  font-size: 20px;
  font-weight: 600;
  color: #333;
  text-align: center;
}

.subtitle {
  margin: 0 0 20px;
  padding: 10px 12px;
  border-radius: 4px;
  background: #f5f7fa;
  color: #666;
  font-size: 12px;
  line-height: 1.6;
  text-align: center;
}

.login-form {
  margin-bottom: 16px;
}

.form-item {
  margin-bottom: 0;
}

.label {
  display: block;
  font-size: 14px;
  color: #333;
  margin-bottom: 6px;
}

.input {
  width: 100%;
  box-sizing: border-box;
  padding: 8px 10px;
  border-radius: 4px;
  border: 1px solid #dcdfe6;
  font-size: 14px;
  outline: none;
}

.input:focus {
  border-color: #409eff;
}

.actions {
  margin-top: 8px;
}

.btn {
  width: 100%;
  height: 40px;
  border: none;
  border-radius: 4px;
  background: #409eff;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
}

.btn[disabled] {
  opacity: 0.6;
  cursor: not-allowed;
}

.result {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}

.result-title {
  font-size: 13px;
  color: #666;
  margin-bottom: 4px;
}

.result-content {
  max-height: 240px;
  overflow: auto;
  background: #f5f7fa;
  padding: 8px;
  border-radius: 4px;
  font-size: 12px;
  line-height: 1.5;
}
</style>
